/**
 * useJiraExportState — manages Jira-specific selection state for export.
 *
 * Handles:
 * - Cloud site, project, and issue type selection
 * - Auto-loading issue types when project changes
 * - Auto-selecting first project when data loads
 */
import { useState, useEffect, useCallback } from 'react';
import { toUserFacingMessage } from '../../../userFacingError.js';
export function useJiraExportState(runtime, provider) {
    const { api: integrationsApi, debug } = runtime;
    const [jiraResources, setJiraResources] = useState([]);
    const [jiraProjects, setJiraProjects] = useState([]);
    const [jiraIssueTypes, setJiraIssueTypes] = useState([]);
    const [selectedCloudId, setSelectedCloudId] = useState('');
    const [selectedProjectKey, setSelectedProjectKey] = useState('');
    const [selectedIssueTypeId, setSelectedIssueTypeId] = useState('');
    // A failed issue-types fetch used to do nothing but log: it did not even
    // clear the stale types, and left an empty Issue Type select with a
    // permanently disabled Export button (EP-186, the sibling EP-099's fix
    // missed). It hid behind a false-passing harness scenario.
    const [issueTypesError, setIssueTypesError] = useState(null);
    const [issueTypesReloadNonce, setIssueTypesReloadNonce] = useState(0);
    // The `${cloudId}/${projectKey}` the issue types were fetched for, '' while a
    // fetch is in flight, so `issueTypesEmpty` means a fetch for THIS project
    // landed empty. There is no loading flag here: without the key, an empty
    // list reads the same while the fetch is pending.
    const [issueTypesFor, setIssueTypesFor] = useState('');
    /** Callback for useExportOAuth to populate Jira data */
    const onJiraDataLoaded = useCallback((data) => {
        debug.log('Jira data loaded:', { resources: data.resources.length, projects: data.projects.length });
        setJiraResources(data.resources);
        setJiraProjects(data.projects);
        if (data.resources.length > 0) {
            setSelectedCloudId(data.resources[0].id);
        }
    }, [runtime]);
    /** Reset all Jira-specific state */
    const resetJiraState = useCallback(() => {
        debug.log('Resetting Jira state');
        setSelectedCloudId('');
        setSelectedProjectKey('');
        setSelectedIssueTypeId('');
        setIssueTypesError(null);
        setIssueTypesFor('');
    }, [runtime]);
    // Load issue types when project changes
    useEffect(() => {
        if (provider !== 'jira' || !selectedCloudId || !selectedProjectKey)
            return;
        const loadIssueTypes = async () => {
            debug.log('Loading issue types for:', { cloudId: selectedCloudId, projectKey: selectedProjectKey });
            setIssueTypesError(null);
            setIssueTypesFor('');
            try {
                const types = await integrationsApi.getJiraIssueTypes(selectedCloudId, selectedProjectKey);
                debug.log('Loaded issue types:', types.length);
                setJiraIssueTypes(types);
                const defaultType = types.find(t => t.name === 'Bug') || types.find(t => t.name === 'Task') || types[0];
                if (defaultType) {
                    setSelectedIssueTypeId(defaultType.id);
                }
                setIssueTypesError(null);
                setIssueTypesFor(`${selectedCloudId}/${selectedProjectKey}`);
            }
            catch (err) {
                debug.error('Error loading issue types:', err);
                // Clear the stale types too: leaving the previous project's types
                // selectable would export into the wrong issue type.
                setJiraIssueTypes([]);
                setSelectedIssueTypeId('');
                setIssueTypesError(toUserFacingMessage(err, "Couldn't load issue types for this project."));
            }
        };
        loadIssueTypes();
    }, [provider, selectedCloudId, selectedProjectKey, issueTypesReloadNonce, runtime]);
    /** Refetch the issue types for the current project after a failure. */
    const retryLoadIssueTypes = useCallback(() => {
        setIssueTypesReloadNonce((n) => n + 1);
    }, [runtime]);
    // Auto-select first project when resources change
    useEffect(() => {
        if (jiraProjects.length > 0 && !selectedProjectKey) {
            debug.log('Auto-selecting first Jira project:', jiraProjects[0].key);
            setSelectedProjectKey(jiraProjects[0].key);
        }
    }, [jiraProjects, selectedProjectKey]);
    return {
        jiraResources,
        jiraProjects,
        jiraIssueTypes,
        selectedCloudId,
        setSelectedCloudId,
        selectedProjectKey,
        setSelectedProjectKey,
        selectedIssueTypeId,
        setSelectedIssueTypeId,
        issueTypesError,
        issueTypesEmpty: !!selectedProjectKey && issueTypesFor === `${selectedCloudId}/${selectedProjectKey}` && jiraIssueTypes.length === 0,
        retryLoadIssueTypes,
        onJiraDataLoaded,
        resetJiraState,
    };
}
//# sourceMappingURL=useJiraExportState.js.map